import apiClient from './axios';

/** Where Ibra decided a turn's inference should happen. */
export type IbraLane =
  | 'local_data'
  | 'plan_and_execute'
  | 'method_only'
  | 'escalate';

export type EvalRunStatus = 'running' | 'completed' | 'failed';

/**
 * Which corpus a run scored.
 *
 * Deliberately not averaged with each other. A critical in `lanes` is a
 * question about the business answered from general knowledge; a critical in
 * `actions` is a figure on an invoice nobody typed. Same word, different
 * failure — so the table filters rather than blends.
 */
export type EvalSuite = 'lanes' | 'actions';

export const SUITES: { value: EvalSuite; label: string; description: string }[] = [
  {
    value: 'lanes',
    label: 'Lane routing',
    description: 'Which lane a question is answered in.',
  },
  {
    value: 'actions',
    label: 'Actions',
    description: 'What Ibra offers to do — mostly drafting invoices.',
  },
];

/**
 * How much a misroute costs, which is not the same for all of them.
 *
 * `critical` is a question about a business answered from general knowledge —
 * the model had none of their figures and answered anyway. Everything else is
 * a wasted round trip or an unnecessary consent prompt.
 */
export type Severity = 'critical' | 'major' | 'minor';

export interface EvalRunSummary {
  suite: EvalSuite;
  id: string;
  model: string;
  status: EvalRunStatus;
  total: number;
  correct: number;
  /** 0–1. */
  accuracy: number;
  critical: number;
  major: number;
  minor: number;
  startedAt: string;
  finishedAt: string | null;
  error: string | null;
}

export interface EvalFailure {
  question: string;
  /** A lane on the lanes suite, an outcome on the actions suite. Read it
   *  through `axisFor(run.suite).label` rather than assuming either. */
  expected: string;
  actual: string;
  severity: Severity;
  reason: string;
  note: string | null;
}

export interface EvalRunDetail extends EvalRunSummary {
  /** Keyed by lane or by outcome, depending on the suite. The column is
   *  called `byLane` because it predates the actions suite. */
  byLane: Record<string, { expected: number; correct: number; recall: number }> | null;
  /** expected → actual → count. */
  confusion: Record<string, Record<string, number>> | null;
  failures: EvalFailure[] | null;
}

export interface LaneCase {
  question: string;
  expected: IbraLane;
  note?: string;
}

export interface LaneSummary {
  windowDays: number;
  totalTurns: number;
  lanes: Array<{ lane: string; turns: number; share: number }>;
  outcomes: Array<{ outcome: string; turns: number; share: number }>;
  planRetryRate: number;
  narrativeDropRate: number;
  analyses: Array<{ analysis: string; turns: number }>;
  medianDurationMs: number | null;
}

export const ibraApi = {
  /**
   * Starts a run and returns before it finishes — fifty model calls take the
   * better part of a minute, which is past most proxy timeouts. Poll `list`
   * until the row leaves `running`.
   *
   * Omit `model` for the configured default. It is free text on purpose: the
   * point of running this from here is trying a model nobody has hardcoded.
   */
  async startEval(suite: EvalSuite, model?: string): Promise<EvalRunSummary> {
    const res = await apiClient.post('/admin/ibra/evals', {
      suite,
      ...(model ? { model } : {}),
    });
    return res.data.data;
  },

  async listEvals(limit = 25, suite?: EvalSuite): Promise<EvalRunSummary[]> {
    const res = await apiClient.get('/admin/ibra/evals', {
      params: { limit, ...(suite ? { suite } : {}) },
    });
    return res.data.data;
  },

  async getEval(id: string): Promise<EvalRunDetail> {
    const res = await apiClient.get(`/admin/ibra/evals/${id}`);
    return res.data.data;
  },

  /** The questions being scored. A score is not reviewable without them. */
  async corpus(suite: EvalSuite = 'lanes'): Promise<{ total: number; cases: LaneCase[] }> {
    const res = await apiClient.get('/admin/ibra/evals/corpus', {
      params: { suite },
    });
    return res.data.data;
  },

  /** Live lane mix from real traffic — distinct from the eval, which scores a
   *  fixed corpus. */
  async laneTelemetry(days = 30): Promise<LaneSummary> {
    const res = await apiClient.get('/admin/ibra/telemetry/lanes', {
      params: { days },
    });
    return res.data.data;
  },
};

/** One feature nugget's numbers over the window, or one app's total. */
export interface NuggetCounts {
  audience: string;
  shown: number;
  opened: number;
  dismissed: number;
  pushed: number;
  /** Opened ÷ shown, 0–1. Null when nothing was shown. */
  openRate: number | null;
}

export interface NuggetStats {
  windowDays: number;
  audiences: NuggetCounts[];
  nuggets: (NuggetCounts & { guideId: string; title: string })[];
  /** How-to questions answered from the guides. Business turns are marked
   *  as how-tos but not by guide, so they arrive as a count. */
  howTos: {
    consumer: { guideId: string; title: string; asked: number }[];
    businessTotal: number;
  };
}

export const nuggetsApi = {
  /** Counts only — see `GET /admin/ibra/telemetry/nuggets`. */
  async stats(days = 30): Promise<NuggetStats> {
    const res = await apiClient.get('/admin/ibra/telemetry/nuggets', { params: { days } });
    return res.data.data;
  },
};

export const LANES: IbraLane[] = [
  'local_data',
  'plan_and_execute',
  'method_only',
  'escalate',
];

export const laneLabel = (lane: string): string =>
  ({
    local_data: 'Lookup',
    plan_and_execute: 'Analysis',
    method_only: 'General',
    escalate: 'Needs consent',
  })[lane] ?? lane;

/**
 * What a turn did, for the actions suite. The same three columns the lane
 * suite uses for lanes — a run row carries whichever axis its suite scored,
 * which is why the detail view reads `suite` before it reads `byLane`.
 */
export const OUTCOMES = ['propose', 'question', 'none'] as const;

export const outcomeLabel = (outcome: string): string =>
  ({
    propose: 'Offered to act',
    question: 'Asked first',
    none: 'Did nothing',
  })[outcome] ?? outcome;

/** The axis a run was scored on, and how to read it. */
export const axisFor = (suite: EvalSuite) =>
  suite === 'actions'
    ? { keys: OUTCOMES as readonly string[], label: outcomeLabel }
    : { keys: LANES as readonly string[], label: laneLabel };
